/**
 * @flow
 */

import React, { useMemo } from 'react';
import gql from 'graphql-tag';
import styled from 'styled-components';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import Fuse from 'fuse.js';
import { FormattedMessage } from 'react-intl';
import { Loader } from 'semantic-ui-react';

import { Table, Avatar } from '../../../components';
import SwitchSalonButton from '../containers/SwitchSalonButton';

import type { Salon } from '../../../type';

export const GET_SALON_LIST_QUERY = gql`
    query getSalonList($resolveRole: String) {
        viewer(resolveRole: $resolveRole) {
            id
            ... on Sysadmin {
                salonList {
                    id
                    name
                    administrator {
                        id
                        firstName
                        lastName
                        phone
                        email
                    }
                    masterCount
                }
            }
        }
    }
`;

const FUSE_OPTIONS = {
    keys: [
        'name',
        'administrator.firstName',
        'administrator.lastName',
        'administrator.phone',
        'administrator.email',
    ],
    shouldSort: true,
    location: 0,
    distance: 0,
    threshold: 0.0,
};

type SalonListProps = {
    salons: Array<Salon>,
    loading: boolean,
};

type SalonRowProps = {
    salon: Salon,
};

const NameCell = styled(Table.Cell)`
    .ui.table td& {
        padding-top: 0;
        padding-bottom: 0;
    }
`;

const EmptyMessage = () => (
    <Table.Row className="transparent">
        <Table.Cell colSpan={3} textAlign="center">
            <FormattedMessage
                id="pages.salonList.noSalons"
                defaultMessage="No salon found"
            />
        </Table.Cell>
    </Table.Row>
);

const OwnerCell = ({ owner }) => (
    <Table.Cell>
        {[
            `${owner.firstName} ${owner.lastName} `,
            '(',
            owner.phone ? `${owner.phone}, ` : '',
            owner.email,
            ')',
        ].join('')}
    </Table.Cell>
);

const SalonRow = ({ salon }: SalonRowProps) => (
    <Table.Row>
        <NameCell>
            <Avatar name={salon.name} size={24} spaced="right" round />{' '}
            {salon.name}
        </NameCell>
        <OwnerCell owner={salon.administrator} />
        <Table.Cell>{salon.masterCount}</Table.Cell>
        <Table.Cell>
            <SwitchSalonButton
                salon={salon.id}
                salonName={salon.name}
                primary
                compact
            >
                <FormattedMessage
                    id="pages.salonList.swithSalon"
                    defaultMessage="Login"
                />
            </SwitchSalonButton>
        </Table.Cell>
    </Table.Row>
);

const SalonList = (props: SalonListProps) => {
    const searchQuery = useSelector(({ salon }) => salon.get('searchQuery'));

    const { data, loading } = useQuery(GET_SALON_LIST_QUERY, {
        variables: { resolveRole: 'SYSADMIN' },
        fetchPolicy: 'cache-and-network',
    });

    const salonList = !loading && data && data.viewer ? data.viewer.salonList : undefined;

    const salonSearch = useMemo(
        () => (salonList ? new Fuse(salonList, FUSE_OPTIONS) : null),
        [salonList],
    );

    const salons =
        salonSearch && searchQuery ? salonSearch.search(searchQuery) : salonList;

    if (loading) {
        return <Loader size="big" inline="centered" active />;
    }

    return (
    <Table celled selectable striped fixed>
        <Table.Header>
            <Table.Row>
                <Table.HeaderCell>
                    <FormattedMessage
                        id="pages.salonList.column.name"
                        defaultMessage="Name"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell>
                    <FormattedMessage
                        id="pages.salonList.column.owner"
                        defaultMessage="Owner"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell>
                    <FormattedMessage
                        id="pages.salonList.column.masters"
                        defaultMessage="Masters"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell>
                    <FormattedMessage
                        id="pages.salonList.column.actions"
                        defaultMessage="Actions"
                    />
                </Table.HeaderCell>
            </Table.Row>
        </Table.Header>
        <Table.Body>
            {isEmpty(salons) ? (
                <EmptyMessage />
            ) : (
                salons.map(salon => <SalonRow key={salon.id} salon={salon} />)
            )}
        </Table.Body>
    </Table>
    );
};

export default SalonList;
